import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { commitmentCategoryOrder } from '@app/app.constants';
import {
  ContractCommitmentCriteria,
  ContractCommitmentCriteriaImmutable,
  JsonContractCommitmentCriteriaImmutable,
} from '@core/models/contract-commitment-criteria.model';
import { ContractCommitment, ContractCommitmentImmutable, JsonContractCommitmentImmutable } from '@core/models/contract-commitment.model';
import { ContractItem } from '@core/models/contract-item.model';
import { Contract } from '@core/models/contract.model';
import { CommitmentService } from '@core/services/commitment/commitment.service';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { ContractService } from '@core/services/contract/contract.service';
import { CommitmentGroupComponent } from '@shared/contract/contract-item/contract-commitment/category-group-commitment-component/commitment-group.component';
import moment from 'moment/moment';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-contract-commitment',
  templateUrl: './contract-commitment.component.html',
  styleUrls: ['./contract-commitment.component.scss'],
})
export class ContractCommitmentComponent implements OnInit, AfterViewInit {
  public componentDestroyed$: Subject<void> = new Subject();
  public groupedCommitmentList: { category: string; commitments: ContractCommitment[] }[];
  public groupedCriteriaList: { category: string; commitmentCriterias: ContractCommitmentCriteria[] }[];
  public groupedTotalList: { category: string; commitmentCriterias: ContractCommitmentCriteria[] }[];
  public groupedCriteriaDateList: { category: string; commitmentCriterias: ContractCommitmentCriteria[] }[];
  public projectedRateFormControl = new FormControl(0, [
    Validators.required,
    Validators.min(0),
    Validators.max(100),
    this.projectedRateValidator(),
  ]);

  protected groupCriteriaValues: number[] = [];
  protected groupCriteriaValue: number = 0;
  protected immutableCommitmentList: ContractCommitmentImmutable[];
  protected immutableCriteriaList: ContractCommitmentCriteriaImmutable[];
  protected editionMode = false;
  protected isPastYearContract = false;
  protected signedContractStateValue = [3, 5, 8, 10, 13, 15];

  @Input() commitmentList: any;
  @Input() commitmentCriteriaList: any;
  @Input() contract: Contract;
  @Input() contractItem: ContractItem;
  @Input() checkSignedFn!: (stateId: number | null | undefined) => boolean;

  @ViewChildren(CommitmentGroupComponent) commitmentGroups!: QueryList<CommitmentGroupComponent>;

  constructor(
    private groupCriteriaValueDetector: ChangeDetectorRef,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    protected commitmentService: CommitmentService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private contractItemService: ContractService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService
  ) {
    iconRegistry.addSvgIcon('content-copy', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/content-copy.svg'));
    iconRegistry.addSvgIcon('edit', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/edit.svg'));
    iconRegistry.addSvgIcon('delete-bin', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-bin.svg'));
    iconRegistry.addSvgIcon('add-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/add-circle-fill.svg'));
    iconRegistry.addSvgIcon('delete-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-circle-fill.svg'));
  }

  ngOnInit(): void {
    this.cloneCommitments();
    this.cloneCriterias();
    this.groupCommitmentsByCategory();
    this.groupCommitmentsCriteriaByCategory();
    if (this.contract && this.contract?.stateContractYear) {
      this.isPastYearContract = moment(this.contract?.stateContractYear).isBefore(moment(), 'year');
    }
    this.checkForFirstCalcul();
    this.initForm();

    this.cdr.detectChanges();
  }

  ngAfterViewInit() {}

  trackByCategory(_index: number, group: { category: string }) {
    return group.category;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  private initForm() {
    this.projectedRateFormControl.setValue(this.contractItem?.projectedRate ?? 0);
  }

  public projectedRateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const groupCriteriaValue = this.groupCriteriaValue;

      if (value === null || value === undefined || groupCriteriaValue === null || groupCriteriaValue === undefined) {
        return null;
      }

      if (value < groupCriteriaValue) {
        return { projectedRateTooLow: true };
      }

      return null; // ✅ valide
    };
  }

  onProjectedRateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = Number(input.value);

    if (value < 0) {
      value = 0;
    } else if (value > 100) {
      value = 100;
    }

    this.projectedRateFormControl.setValue(value, { emitEvent: false });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  // Fonction pour grouper les engagements par catégorie, et ordonner les sous niveaux
  public groupCommitmentsByCategory() {
    const grouped = (this.commitmentList || []).reduce((acc, commitment) => {
      const category = commitment.template.category;

      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(commitment);

      return acc;
    }, {});

    // Créer une map pour accéder plus facilement aux engagements par id
    const commitmentMap = new Map<number, any>();
    this.commitmentList?.forEach(commitment => {
      commitmentMap.set(commitment.id, commitment);
    });

    Object.keys(grouped)?.forEach(category => {
      const sortedCommitments = [];
      const parentMap = new Map<number, any[]>();

      // Separer les engagements avec/sans parent
      grouped[category].forEach(commitment => {
        if (commitment.template.parent !== null) {
          if (!parentMap.has(commitment.template.parent)) {
            parentMap.set(commitment.template.parent, []);
          }
          parentMap.get(commitment.template.parent).push(commitment);
        } else {
          sortedCommitments.push(commitment);
        }
      });

      // Retrier les engagements par ordre croissant de template.id
      // (utile pour les niv 1 afin d'avoir toujours la meme interface)
      sortedCommitments.sort((a, b) => a.template.id - b.template.id);

      // Déplacer les enfants après leur parent
      for (let i = 0; i < sortedCommitments.length; i++) {
        const commitment = sortedCommitments[i];

        if (parentMap.has(commitment.template.id)) {
          sortedCommitments.splice(i + 1, 0, ...parentMap.get(commitment.template.id));
        }
      }

      grouped[category] = sortedCommitments;
    });

    this.groupedCommitmentList = Object.keys(grouped)
      .map(category => ({
        category,
        commitments: grouped[category],
      }))
      .sort((a, b) => commitmentCategoryOrder.indexOf(a.category) - commitmentCategoryOrder.indexOf(b.category));
  }

  public groupCommitmentsCriteriaByCategory() {
    {
      if (this.commitmentCriteriaList.length === 0) return;

      const baseCategory: ContractCommitmentCriteria[] = [];
      const customCategory: ContractCommitmentCriteria[] = [];
      const exclusionCategory: ContractCommitmentCriteria[] = [];
      const totalCategory: ContractCommitmentCriteria[] = [];
      let dateCategory: ContractCommitmentCriteria[] = [];

      this.commitmentCriteriaList?.forEach(criteria => {
        if (criteria.template.baseCategory === 1 && !criteria.template?.datetimeInput) {
          baseCategory.push(criteria);
        }
        if (criteria.template.customCategory === 1 && !criteria.template?.datetimeInput) {
          customCategory.push(criteria);
        }
        if (criteria.template.exclusionCategory === 1 && !criteria.template?.datetimeInput) {
          exclusionCategory.push(criteria);
        }

        if (criteria.template.totalCategory === 1 && !criteria.template?.datetimeInput) {
          totalCategory.push(criteria);
        }

        if (criteria.template.datetimeInput === 1) {
          dateCategory.push(criteria);
        }
      });

      this.groupedTotalList = [
        {
          category: 'totalCategory',
          commitmentCriterias: totalCategory,
        },
      ];

      this.groupedCriteriaDateList = [
        {
          category: 'dateCategory',
          commitmentCriterias: dateCategory,
        },
      ];

      this.groupedCriteriaList = [
        {
          category: 'exclusionCategory',
          commitmentCriterias: exclusionCategory,
        },
        {
          category: 'baseCategory',
          commitmentCriterias: baseCategory,
        },
        {
          category: 'customCategory',
          commitmentCriterias: customCategory,
        },
      ];
    }
  }

  public checkForFirstCalcul() {
    // if one of the criteria value2 is null and is template as doubleValue to 1 (true),
    // we need to get the calculated data
    if (this.commitmentCriteriaList?.some(criteria => criteria.template.doubleValue === 1 && criteria.value2 === null)) {
      this.callUpdateGroupCriteria(this.groupCriteriaValue, 0);
    }
  }

  public saveForms() {
    this.saveCommitments();
  }

  public cloneCommitments() {
    this.immutableCommitmentList = this.commitmentList?.map(commitment => {
      const jsonCommitmentClone: JsonContractCommitmentImmutable = {
        id: commitment.id,
        value: commitment.value,
        contractItemId: commitment.contractItemId,
        template: commitment.template,
      };

      return new ContractCommitmentImmutable(jsonCommitmentClone);
    });
  }

  public cloneCriterias() {
    this.immutableCriteriaList = this.commitmentCriteriaList?.map(criteria => {
      const jsonCriteriaClone: JsonContractCommitmentCriteriaImmutable = {
        id: criteria.id,
        value: criteria.value,
        value2: criteria.value2,
        contractItemId: criteria.contractItemId,
        datetimeValue: criteria.datetimeValue,
        template: criteria.template,
      };

      return new ContractCommitmentCriteriaImmutable(jsonCriteriaClone);
    });
  }

  protected updateGroupCriteria(value: any, index: number) {
    this.groupCriteriaValues[index] = value;
    this.groupCriteriaValue = this.groupCriteriaValues.reduce((total, current) => total + (current || 0), 0);
    this.groupCriteriaValueDetector.detectChanges();
    this.editionMode = true;
  }

  protected callUpdateGroupCriteria(value: any, index: number) {
    this.commitmentService
      .getComputeByPayload(this.commitmentList, this.commitmentCriteriaList, this.contractItem.year)
      .subscribe(response => {
        this.commitmentList = response['commitmentList']['commitments'];
        this.commitmentCriteriaList = response['commitmentList']['commitmentCriterias'];
        this.groupCommitmentsByCategory();
        this.groupCommitmentsCriteriaByCategory();
      });
  }

  protected saveCommitments() {
    this.editionMode = true;
    this.contractItem.projectedRate = this.projectedRateFormControl.value ?? 0;

    forkJoin({
      commitments: this.commitmentService.updateCommitments(
        this.contract.id,
        this.contractItem.year,
        this.commitmentList,
        this.commitmentCriteriaList
      ),
      contractItem: this.contractItemService.updateContractItem(this.contractItem),
    })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: response => {
          this.editionMode = false;
          this.cloneCommitments();
          this.cloneCriterias();
          this.updateContractCgvModel();
        },
        error: error => {
          this.editionMode = false;
        },
      });
  }

  public updateContractCgvModel() {
    // Appel synchrone de la méthode parent fournie en @Input
    const isSigned = this.checkSignedFn ? this.checkSignedFn(this.contractItem.contractInformations.state.id) : false;

    this.contractCgvModelUpdateService.updateContractCgvModel(this.contractItem?.id, this.contractItem).subscribe(
      response => {
        if (isSigned) {
          this.snackBar.open('Les données ont bien été enregistrées. Vous avez effectué une modification sur un contrat signé. ', null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-success'],
          });
        } else {
          this.snackBar.open('Les données ont bien été enregistrées.', null, {
            duration: 2000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-success'],
          });
        }
      },
      error => {
        this.snackBar.open("Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support.", null, {
          duration: 2000,
          verticalPosition: 'top',
          panelClass: ['tv-chip-error'],
        });
      }
    );
  }

  protected cancelFormsModification() {
    this.commitmentList = this.immutableCommitmentList;
    this.commitmentCriteriaList = this.immutableCriteriaList;
    this.groupCommitmentsByCategory();
    this.groupCommitmentsCriteriaByCategory();
    this.checkForFirstCalcul();
    this.editionMode = false;
  }

  protected commitmentsChange($event: any) {
    this.editionMode = $event;
    this.commitmentService
      .getComputeByPayload(this.commitmentList, this.commitmentCriteriaList, this.contractItem.year)
      .subscribe(response => {
        this.commitmentList = response['commitmentList']['commitments'];
        this.commitmentCriteriaList = response['commitmentList']['commitmentCriterias'];
        this.groupCommitmentsByCategory();
        this.groupCommitmentsCriteriaByCategory();
      });
  }

  enforceNegativeNctRate(): void {
    const value = this.projectedRateFormControl.value;
    if (value != null && !isNaN(value) && value > 0) {
      this.projectedRateFormControl.setValue(-Math.abs(value));
    }
  }
}
